import React, { useState } from "react";
import { Loader2, MessageCircle } from "lucide-react";
import { maxPlatform } from "../services/maxPlatform";
import { Button } from "./Button";
import { GlassCard } from "./GlassCard";

type MaxChatActivateGateProps = {
  activateDeepLink: string;
  onRecheck: () => Promise<boolean>;
};

/**
 * Hard gate: MAX opens mini-app via ?startapp without activating the bot dialog.
 * User must open the bot chat and send a message (or Start) before we continue.
 */
export function MaxChatActivateGate({ activateDeepLink, onRecheck }: MaxChatActivateGateProps) {
  const [checking, setChecking] = useState(false);
  const [hint, setHint] = useState("");

  const openChat = () => {
    setHint("");
    maxPlatform.openMaxLink(activateDeepLink);
  };

  const recheck = async () => {
    setChecking(true);
    setHint("");
    try {
      const ok = await onRecheck();
      if (!ok) {
        setHint("Чат ещё не активирован. Откройте бота и отправьте «Старт» или любое сообщение.");
      }
    } catch {
      setHint("Не удалось проверить. Попробуйте ещё раз через пару секунд.");
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <GlassCard variant="elevated" padding="lg" className="w-full max-w-sm">
        <div className="flex flex-col items-center text-center gap-3">
          <div className="w-14 h-14 rounded-full bg-indigo-100 flex items-center justify-center">
            <MessageCircle size={28} className="text-indigo-600" />
          </div>
          <h2 className="text-[18px] font-semibold text-gray-900">
            Напишите боту, чтобы продолжить
          </h2>
          <p className="text-[14px] text-gray-600 leading-relaxed">
            MAX не открывает чат с ботом автоматически при входе по ссылке или QR. Откройте чат и
            отправьте сообщение (например, «Старт») — так приложение останется доступным в MAX.
          </p>
          <Button className="w-full mt-1" onClick={openChat}>
            Открыть чат с ботом
          </Button>
          <button
            type="button"
            className="text-[13px] text-indigo-600 font-medium py-2 disabled:opacity-50"
            disabled={checking}
            onClick={() => void recheck()}
          >
            {checking ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" />
                Проверяем…
              </span>
            ) : (
              "Я уже написал"
            )}
          </button>
          {hint ? <p className="text-[12px] text-amber-700">{hint}</p> : null}
        </div>
      </GlassCard>
    </div>
  );
}
